import React, { useMemo, useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import {
  FaUsers,
  FaCalendarAlt,
  FaEnvelope,
  FaMoneyBillWave,
  FaUserCheck,
  FaChartBar,
  FaAddressCard,
  FaCartPlus,
  FaRegCreditCard,
  FaUserCircle,
  FaFileSignature,
  FaRegNewspaper,
  FaImage,
  FaComment,
  FaHourglassHalf,
  FaCalculator,
  FaShoppingBag,
} from "react-icons/fa";
import { IoMdContacts, IoMdSettings } from "react-icons/io";
import {
  GiBlackBelt,
  GiKimono,
  GiPodiumWinner,
  GiPodium,
  GiGong,
} from "react-icons/gi";
import { MdDashboard, MdTimer, MdOutlineInventory } from "react-icons/md";
import { PiTranslateFill } from "react-icons/pi";
import { LuListTodo } from "react-icons/lu";

const cx = (...c) => c.filter(Boolean).join(" ");

const linkIcon = {
  "Tableau de Bord": <MdDashboard />,
  "Dossier d'Inscription": <FaAddressCard />,
  "Fiche Adhérent": <FaUserCircle />,
  Calendrier: <FaCalendarAlt />,
  Messages: <FaEnvelope />,
  Chat: <FaComment />,
  Boutique: <FaCartPlus />,
  "Commandes & Règlements": <FaRegCreditCard />,
  "Techniques Judo": <GiKimono />,
  Actualités: <FaRegNewspaper />,
  Lexique: <PiTranslateFill />,
  "Arbitrage Judo": <GiGong />,
  "Galerie Photos": <FaImage />,
  "Listing Adhérents": <IoMdContacts />,
  "Passage Ceinture": <GiBlackBelt />,
  "Grade CN": <GiBlackBelt />,
  "Suivi Compétitions": <GiPodiumWinner />,
  Palmarès: <GiPodium />,
  ToDoList: <LuListTodo />,
  Événements: <FaCalendarAlt />,
  "Appel Présence": <FaUserCheck />,
  "Dashboard Adhérents": <FaChartBar />,
  "Dashboard Comptabilité": <FaChartBar />,
  "Détails Adhérent": <FaUserCircle />,
  "Listing Utilisateurs": <FaUsers />,
  "Listing Adhérents Admin": <FaUsers />,
  "Règlements Inscriptions": <FaMoneyBillWave />,
  "Dossiers d'Inscription": <FaFileSignature />,
  "Achats Boutique": <FaCartPlus />,
  "Articles Admin": <FaShoppingBag />,
  "Dépenses Recettes": <FaCalculator />,
  Coachings: <MdTimer />,
  "Heures Enseignement": <FaHourglassHalf />,
  Inventaire: <MdOutlineInventory />,
  "Chat Page Admin": <FaComment />,
  "Message Admin": <FaEnvelope />,
  Paramètres: <IoMdSettings />,
};

const linkDescriptions = {
  "Tableau de Bord": "Vue d'ensemble des informations clés.",
  "Dossier d'Inscription": "Inscription, documents et suivi.",
  "Fiche Adhérent": "Profil et informations personnelles.",
  Calendrier: "Événements et inscriptions.",
  Messages: "Boîte de réception et envoi de messages.",
  Chat: "Communication en direct avec le club.",
  Boutique: "Boutique en ligne du club.",
  "Commandes & Règlements": "Suivi des achats et paiements.",
  "Techniques Judo": "Base de techniques et contenus pédagogiques.",
  Actualités: "Publications et informations du club.",
  Lexique: "Termes japonais / français.",
  "Arbitrage Judo": "Règlements et règles d’arbitrage.",
  "Galerie Photos": "Albums et galeries photos.",
  "Listing Adhérents": "Accès au listing pour suivi et gestion.",
  "Passage Ceinture": "Suivi des passages et validations.",
  "Grade CN": "Gestion et suivi des grades CN.",
  "Suivi Compétitions": "Gestion des compétiteurs et résultats.",
  Palmarès: "Historique et palmarès.",
  ToDoList: "Liste de tâches partagée.",
  Événements: "Planification et suivi des événements.",
  "Appel Présence": "Appel et historique de présence.",
  "Dashboard Adhérents": "Statistiques adhérents.",
  "Dashboard Comptabilité": "Statistiques et suivi comptable.",
  "Détails Adhérent": "Détail complet d’un adhérent.",
  "Listing Utilisateurs": "Gestion des utilisateurs et rôles.",
  "Listing Adhérents Admin": "Vue admin complète des adhérents.",
  "Règlements Inscriptions": "Suivi des paiements d’inscription.",
  "Dossiers d'Inscription": "Suivi administratif des dossiers.",
  "Achats Boutique": "Suivi des achats boutique.",
  "Articles Admin": "Gestion des articles et stocks.",
  "Dépenses Recettes": "Comptabilité du club.",
  Coachings: "Gestion des sessions de coaching.",
  "Heures Enseignement": "Suivi des heures enseignants.",
  Inventaire: "Gestion du matériel.",
  "Chat Page Admin": "Messagerie admin.",
  "Message Admin": "Messages admin.",
  Paramètres: "Paramètres généraux du club et du site.",
};

const roleMeta = {
  Adhérent: {
    accent: "from-orange-400 to-orange-600",
    hint: "Espace membre",
  },
  Enseignant: {
    accent: "from-sky-500 to-indigo-600",
    hint: "Espace coach",
  },
  Dirigeant: {
    accent: "from-emerald-500 to-green-600",
    hint: "Espace bureau",
  },
};

const Features = () => {
  const [activeRole, setActiveRole] = useState("Adhérent");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const sections = useMemo(
    () => ({
      Adhérent: [
        "Tableau de Bord",
        "Dossier d'Inscription",
        "Fiche Adhérent",
        "Calendrier",
        "Messages",
        "Chat",
        "Boutique",
        "Commandes & Règlements",
        "Actualités",
        "Galerie Photos",
        "Lexique",
        "Arbitrage Judo",
        "Techniques Judo",
      ],
      Enseignant: [
        "Listing Adhérents",
        "Appel Présence",
        "Passage Ceinture",
        "Grade CN",
        "Événements",
        "Suivi Compétitions",
        "Palmarès",
        "ToDoList",
      ],
      Dirigeant: [
        "Dashboard Adhérents",
        "Dashboard Comptabilité",
        "Détails Adhérent",
        "Listing Utilisateurs",
        "Listing Adhérents Admin",
        "Règlements Inscriptions",
        "Dossiers d'Inscription",
        "Achats Boutique",
        "Articles Admin",
        "Dépenses Recettes",
        "Coachings",
        "Heures Enseignement",
        "Inventaire",
        "Chat Page Admin",
        "Message Admin",
        "Paramètres",
      ],
    }),
    [],
  );

  const items = useMemo(() => {
    const list = sections[activeRole] || [];
    const q = query.trim().toLowerCase();

    if (!q) return list;

    return list.filter((name) => {
      const desc = linkDescriptions[name] || "";
      return name.toLowerCase().includes(q) || desc.toLowerCase().includes(q);
    });
  }, [activeRole, query, sections]);

  const meta = roleMeta[activeRole];

  return (
    <section className="bg-white py-16 sm:py-20 dark:bg-slate-950">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl">
          <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
            Sections
          </div>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl dark:text-white">
            Découvrez les outils disponibles selon les rôles
          </h2>
          <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
            Chaque profil dispose de sections adaptées à ses usages : membre,
            enseignant ou dirigeant.
          </p>
        </div>

        <div className="mt-10 rounded-[32px] border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-white/10 dark:bg-slate-900 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {Object.keys(sections).map((role) => {
                const isActive = role === activeRole;
                const count = sections[role]?.length || 0;
                const accent =
                  roleMeta[role]?.accent || "from-slate-400 to-slate-600";

                return (
                  <button
                    key={role}
                    onClick={() => {
                      setActiveRole(role);
                      setQuery("");
                    }}
                    className={cx(
                      "rounded-full px-4 py-2 text-sm font-semibold transition",
                      isActive
                        ? `bg-gradient-to-r ${accent} text-white`
                        : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800",
                    )}
                  >
                    {role}
                    <span
                      className={cx(
                        "ml-2 rounded-full px-2 py-0.5 text-xs",
                        isActive
                          ? "bg-white/20"
                          : "bg-slate-100 dark:bg-slate-800",
                      )}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="relative w-full max-w-md">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Rechercher dans ${activeRole}…`}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-slate-900 placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
              />
            </div>
          </div>

          <div className="mt-4 text-sm text-slate-600 dark:text-slate-300">
            <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-950 dark:text-slate-200">
              {meta?.hint}
            </span>
          </div>
        </div>

        <ul className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((name) => (
            <li key={name}>
              <button
                // onClick={() =>
                //   setSelected({ title: name, desc: linkDescriptions[name] })
                // }
                className="w-full rounded-[28px] border border-slate-200 bg-white p-5 text-left shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-lg dark:border-white/10 dark:bg-slate-900"
              >
                <div className="flex items-start gap-4">
                  <div
                    className={cx(
                      "grid h-12 w-12 shrink-0 place-items-center rounded-2xl text-white",
                      "bg-gradient-to-r",
                      meta?.accent || "from-yellow-400 to-orange-500",
                    )}
                  >
                    <span className="text-xl">
                      {linkIcon[name] || <FaUsers />}
                    </span>
                  </div>

                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 dark:text-white">
                      {name}
                    </div>
                    <div className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
                      {linkDescriptions[name]}
                    </div>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>

        {selected && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
            <div className="w-full max-w-lg rounded-[28px] border border-slate-200 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-slate-950">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-lg font-bold text-slate-900 dark:text-white">
                    {selected.title}
                  </div>
                  <div className="mt-2 text-sm leading-7 text-slate-700 dark:text-slate-200">
                    {selected.desc}
                  </div>
                </div>

                <button
                  onClick={() => setSelected(null)}
                  className="grid h-10 w-10 place-items-center rounded-2xl bg-slate-100 transition hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
                  aria-label="Fermer"
                >
                  <FaTimes className="text-slate-700 dark:text-slate-200" />
                </button>
              </div>

              <div className="mt-5 text-xs text-slate-500 dark:text-slate-400">
                Accessible selon le rôle attribué dans l’application.
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Features;
